import { Directive, ElementRef, HostListener, Inject, Input, LOCALE_ID, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { format, parse } from 'date-fns';
import { SlValueAccessor } from './sl-value-accessor';
import PhoneNumber from 'awesome-phonenumber';

@Directive({
  selector: 'sl-input, sl-radio-group, sl-textarea, [slControl]',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: SlInputValueAccessorDirective, multi: true }]
})
export class SlInputValueAccessorDirective extends SlValueAccessor<HTMLInputElement> implements ControlValueAccessor {
  
  defaultCountryCode = 'DK';

  constructor(element: ElementRef<HTMLInputElement>, renderer: Renderer2, @Inject(LOCALE_ID) locale: string) {
    super(element, renderer, 'value');

    const elements = locale.split('_');
    if(elements.length > 1) {
      this.defaultCountryCode = elements[1];
    }
  }

  writeValue(value: string | Date | number) {
    this.applyValue(value);
  }

  @HostListener('slBlur', ['$event'])
  handleBlur(_: CustomEvent) {
    this.onTouched();
  }

  @HostListener('slInput', ['$event'])
  handleInput(event: CustomEvent) {
    let value = this.parseValue(event);

    this.onChange(value);
  }

  @HostListener('valueChange', ['$event'])
  handleValueChange(event: CustomEvent<any>): void {
    let value = this.parseValue(event);
    this.applyValue(value);
    this.onChange(value);
  }

  private parsePhone(value: string) {
    const pn = new PhoneNumber(value, this.defaultCountryCode);
    if(pn.isValid()) {
      value = pn.getNumber();
    }
    return value;
  }

  private formatPhone(value: string) {
    if(!value) return '';
    const pn = new PhoneNumber(value, this.defaultCountryCode);
    if(pn.isValid()) {
      value = pn.getNumber('international');
    }
    return value;
  }

  private applyValue(value: string | number | Date) {
    const type = this.element.nativeElement.getAttribute('type');
    
    if(value == null) {
      switch(type) {
        case 'date':
          value = new Date();
          break;
        case 'number':
          value = 0;
          break;
        default:
          value = '';
      }
    }

    if (value instanceof Date) {
      const valid = !isNaN(value.getTime());
      switch (type) {
        case 'time':
          value = valid ? format(value, 'HH:mm') : '00:00';
          break;
        case 'date':
        default:
          value = valid ? format(value, 'yyyy-MM-dd') : '1970-01-01';
          break;

      }
    }

    if (typeof (value) === 'number') {
      value = value.toString();
    }

    if (type === 'phone') {
      value = this.formatPhone(value);
    }
    
    super.writeValue(value);
  }

  private parseValue(event: CustomEvent<any>) {
    let value = event.detail ?? this.element.nativeElement.value;
    event.stopPropagation();

    if (this.element.nativeElement.getAttribute('type') === 'date') {
      value = parse(value as string, 'yyyy-MM-dd', new Date());
    }

    if (this.element.nativeElement.getAttribute('type') === 'time') {
      value = parse(value as string, 'H:mm', new Date());
    }

    if (this.element.nativeElement.getAttribute('type') === 'number') {
      value = parseFloat(value);
    }

    if (this.element.nativeElement.getAttribute('type') === 'phone') {
      value = this.parsePhone(value);
    }
    return value;
  }
}